// Shared advisor-matching data + logic — used by the standalone Swipe screen
// (screens/SwipeScreen.js) and the in-chat matching flow (screens/swipe/chatSwipeFlow.js)
// so both share one roster, one set of question options, and one scoring function.

import imgAmanda from '../screens/swipe/advisor-amanda.jpg';
import imgJames from '../screens/swipe/advisor-james.jpg';
import imgMichael from '../screens/swipe/advisor-michael.jpg';
import imgPriya from '../screens/swipe/advisor-priya.jpg';
import imgDavid from '../screens/swipe/advisor-david.jpg';
import imgSarah from '../screens/swipe/advisor-sarah.jpg';
import imgMeiling from '../screens/swipe/advisor-meiling.jpg';
import imgOmar    from '../screens/swipe/advisor-omar.jpg';
import imgKayla   from '../screens/swipe/advisor-kayla.jpg';

// ─── ADVISOR TYPE CLASSIFICATION ────────────────────────────────────────────
// Classifies intent text into the broad advisor category the customer needs.
// Used to surface the right advisor tier — bank (retail), wealth (HNW), or
// business (SMB/entrepreneur) — before the detailed specialty scoring runs.

export function classifyAdvisorType(intent) {
  const t = (intent || '').toLowerCase();
  if (/business|company|self.?employ|entrepreneur|startup|incorporat|smb/.test(t)) return 'business';
  if (/invest|portfolio|market|wealth|wm\b|rrsp|tfsa|high.net|over.?1m|high.net.worth/.test(t)) return 'wealth';
  return 'bank';
}

// ─── INTENT → CATEGORY + FOLLOW-UP QUESTION ────────────────────────────────
// category is the machine-usable tag matchAdvisors() scores against; question/options
// are the copy shown to the customer.

export function deriveFollowUp(intent) {
  const t = (intent || '').toLowerCase();
  if (t.includes('home') || t.includes('mortgage') || t.includes('refinanc') || t.includes('buy') || t.includes('house') || t.includes('property')) {
    return {
      category: 'home',
      question: 'What are you looking to do with your home financing?',
      options: ['Buy my first home', 'Buy an investment property', 'Renew my mortgage', 'Refinance for a better rate'],
    };
  }
  if (t.includes('invest') || t.includes('portfolio') || t.includes('market') || t.includes('money working') || t.includes('wealth') || t.includes('rrsp') || t.includes('tfsa')) {
    return {
      category: 'invest',
      question: 'What matters most to you right now?',
      options: ['Maximize growth', 'Steady & balanced returns', 'Protect what I have', 'I want a professional to guide me'],
    };
  }
  if (t.includes('retire') || t.includes('retirement') || t.includes('pension')) {
    return {
      category: 'retire',
      question: 'When do you hope to retire?',
      options: ['Within 5 years', '5–15 years from now', '15+ years away', 'Already retired — managing income'],
    };
  }
  if (t.includes('debt') || t.includes('loan') || t.includes('credit') || t.includes('spend')) {
    return {
      category: 'debt',
      question: 'What type of debt are you looking to tackle?',
      options: ['Mortgage', 'Credit cards & personal loans', 'Student loans', 'Mix of multiple types'],
    };
  }
  if (t.includes('protect') || t.includes('insurance') || t.includes('family') || t.includes('estate') || t.includes('will')) {
    return {
      category: 'protect',
      question: 'What are you primarily looking to protect?',
      options: ["My family's income if I can't work", 'My health & critical illness', 'My estate & legacy', 'Not sure yet — want to explore'],
    };
  }
  if (t.includes('business') || t.includes('company') || t.includes('self-employ') || t.includes('entrepreneur')) {
    return {
      category: 'business',
      question: 'What stage is your business at?',
      options: ['Starting up', 'Growing (1–5 years in)', 'Established', 'Planning to sell or exit'],
    };
  }
  if (t.includes('plan') || t.includes('second opinion') || t.includes('advice') || t.includes('review') || t.includes('not sure')) {
    return {
      category: 'general',
      question: "What's weighing on you most financially right now?",
      options: ["I don't have a real plan", "I'm not saving enough", 'I want a second opinion on my situation', "I'm worried about the future"],
    };
  }
  return {
    category: 'general',
    question: 'What would make the biggest difference for you right now?',
    options: ['Getting a clear financial plan', 'Growing or protecting my savings', 'Reducing stress around money', 'Preparing for something big coming up'],
  };
}

// ─── SHARED QUESTION OPTION SETS ────────────────────────────────────────────

export const SNAPSHOT_OPTIONS = [
  { id: 'under50k', label: 'Under $50,000', sub: 'Building my foundation' },
  { id: '50to250k', label: '$50,000 – $250,000', sub: 'Growing steadily' },
  { id: '250kto1m', label: '$250,000 – $1,000,000', sub: 'Established savings' },
  { id: 'over1m', label: '$1,000,000+', sub: 'High-net-worth' },
  { id: 'preferNotToSay', label: 'I prefer not to say', sub: "That's okay — we'll still find your match" },
];

export const STYLE_OPTIONS = [
  { id: 'direct', label: 'Get to the point — direct and efficient', icon: '⚡' },
  { id: 'context', label: 'Walk me through it — I like context', icon: '🗺️' },
  { id: 'patient', label: "Be patient with me — I'm still learning", icon: '🌱' },
  { id: 'challenge', label: "Challenge my thinking — don't just agree with me", icon: '🔍' },
];

export const MEETING_OPTIONS = [
  { id: 'in-person', label: 'In-person', icon: '🏦', sub: 'Visit a TD branch near you' },
  { id: 'video', label: 'Video call', icon: '💻', sub: 'Meet from home, anytime' },
  { id: 'phone', label: 'Phone', icon: '📞', sub: 'Quick and convenient' },
];

export const LANGUAGE_OPTIONS = ['English', 'French', 'Cantonese', 'Mandarin', 'Punjabi', 'Spanish', 'Other'];

// ─── ADVISOR ROSTER ──────────────────────────────────────────────────────────
// Fictional composites modeled on realistic Canadian retail-bank advisor roles and
// designations (Financial Advisor / Financial Planner / Small Business Advisor / Wealth
// Advisor; CFP®/PFP®/CIM®/FCSI®/CLU®-style credentials) — not real individuals.
// `specialties` are deriveFollowUp() category tags this advisor scores well against.
// `styleStrength` are STYLE_OPTIONS ids this advisor is known for (scoring bonus).
// `snapshotBonus` maps SNAPSHOT_OPTIONS ids to a scoring bonus for that bracket.

export const ADVISORS = [
  {
    id: 'amanda-l',
    name: 'Amanda L.',
    title: 'Senior Financial Advisor',
    branch: 'Dundas Branch',
    address: '180 Dundas St W, Suite 100, Toronto, ON',
    certs: ['CFP', 'CFA'],
    languages: ['English', 'French'],
    bio: '10+ years of experience specializing in home financing and wealth management.',
    photo: imgAmanda,
    rating: 4.9,
    reviews: 124,
    advisorType: 'bank',
    specialties: ['home'],
    styleStrength: ['direct'],
    snapshotBonus: {},
    styleMatch: {
      'Get to the point — direct and efficient': "Amanda's known for efficient, no-fluff sessions — clients consistently say she respects your time.",
      'Walk me through it — I like context': 'Amanda takes the time to explain the full picture. Clients say they always leave knowing exactly why each decision was made.',
      "Be patient with me — I'm still learning": 'Amanda specializes in first-time clients and explains things step-by-step without rushing.',
      "Challenge my thinking — don't just agree with me": "Amanda's straight-talking style means she'll push back when it matters and offer alternative perspectives.",
    },
  },
  {
    id: 'james-t',
    name: 'James T.',
    title: 'Financial Planning Advisor',
    branch: 'Bay Street Branch',
    address: '120 Bay St, Toronto, ON',
    certs: ['CFP'],
    languages: ['English'],
    bio: 'Specialist in retirement planning and investment portfolio optimization.',
    photo: imgJames,
    rating: 4.8,
    reviews: 87,
    advisorType: 'bank',
    specialties: ['retire', 'invest'],
    styleStrength: ['context'],
    snapshotBonus: {},
    styleMatch: {
      'Get to the point — direct and efficient': 'James runs focused, agenda-driven sessions — you leave with a clear action list every time.',
      'Walk me through it — I like context': 'James is known for his thorough explanations and will walk you through every scenario before recommending a path.',
      "Be patient with me — I'm still learning": 'James has a reputation for breaking down complex retirement concepts into plain language.',
      "Challenge my thinking — don't just agree with me": 'James will proactively stress-test your assumptions and bring data to back his recommendations.',
    },
  },
  {
    id: 'michael-r',
    name: 'Michael R.',
    title: 'Wealth Management Advisor',
    branch: 'King West Branch',
    address: '350 King St W, Toronto, ON',
    certs: ['CFA'],
    languages: ['English', 'Mandarin'],
    bio: 'Focused on high-net-worth clients with complex investment strategies.',
    photo: imgMichael,
    rating: 4.7,
    reviews: 63,
    advisorType: 'wealth',
    specialties: ['invest'],
    styleStrength: ['challenge'],
    snapshotBonus: { over1m: 25, '250kto1m': 12 },
    styleMatch: {
      'Get to the point — direct and efficient': "Michael focuses on high-impact decisions only and won't waste your time on minor details.",
      'Walk me through it — I like context': 'Michael builds comprehensive views of your full financial picture before making any recommendation.',
      "Be patient with me — I'm still learning": "Michael adapts to every client's experience level and has worked with many first-time investors.",
      "Challenge my thinking — don't just agree with me": "Michael is known for taking a contrarian view when the data supports it — he won't tell you what you want to hear.",
    },
  },
  {
    id: 'priya-n',
    name: 'Priya N.',
    title: 'Small Business Advisor',
    branch: 'Queen Street Branch',
    address: '410 Queen St E, Toronto, ON',
    certs: [],
    languages: ['English', 'Punjabi'],
    bio: '8 years helping owners finance, launch, and grow small businesses.',
    photo: imgPriya,
    rating: 4.8,
    reviews: 71,
    advisorType: 'business',
    specialties: ['business'],
    styleStrength: ['direct'],
    snapshotBonus: {},
    styleMatch: {
      'Get to the point — direct and efficient': 'Priya keeps business meetings tight and actionable — she knows your time is your business.',
      'Walk me through it — I like context': "Priya walks through financing options and cash flow scenarios in detail before you commit to anything.",
      "Be patient with me — I'm still learning": 'Priya works with a lot of first-time business owners and explains banking basics without jargon.',
      "Challenge my thinking — don't just agree with me": "Priya will tell you honestly if a business plan needs work before she recommends financing it.",
    },
  },
  {
    id: 'david-k',
    name: 'David K.',
    title: 'Insurance & Estate Planning Advisor',
    branch: 'Yonge & Eglinton Branch',
    address: '2300 Yonge St, Toronto, ON',
    certs: ['CLU', 'CFP'],
    languages: ['English', 'French'],
    bio: 'Helps families protect income, health, and legacy with the right coverage.',
    photo: imgDavid,
    rating: 4.9,
    reviews: 58,
    advisorType: 'bank',
    specialties: ['protect'],
    styleStrength: ['patient'],
    snapshotBonus: {},
    styleMatch: {
      'Get to the point — direct and efficient': 'David gives you the short list of coverage that actually matters for your situation — no upselling.',
      'Walk me through it — I like context': 'David walks through every "what if" scenario so you understand exactly what each policy covers.',
      "Be patient with me — I'm still learning": "David specializes in first-time coverage conversations and never rushes a family's decision.",
      "Challenge my thinking — don't just agree with me": "David will point out gaps in a plan directly, even when it's not what you expected to hear.",
    },
  },
  {
    id: 'sarah-m',
    name: 'Sarah M.',
    title: 'Financial Wellness Advisor',
    branch: 'Scarborough Town Centre Branch',
    address: '300 Borough Dr, Scarborough, ON',
    certs: ['PFP'],
    languages: ['English', 'Cantonese', 'Mandarin'],
    bio: 'Focuses on debt strategy, budgeting, and building a realistic financial plan.',
    photo: imgSarah,
    rating: 4.9,
    reviews: 96,
    advisorType: 'bank',
    specialties: ['debt', 'general'],
    styleStrength: ['patient', 'context'],
    snapshotBonus: { under50k: 15, preferNotToSay: 8 },
    styleMatch: {
      'Get to the point — direct and efficient': "Sarah will give you a clear, no-judgment plan for tackling debt without a long lecture.",
      'Walk me through it — I like context': "Sarah is known for laying out exactly how each dollar move affects your timeline before you decide.",
      "Be patient with me — I'm still learning": "Sarah specializes in first-time budgeting conversations and meets you exactly where you're at.",
      "Challenge my thinking — don't just agree with me": "Sarah will tell you directly if a spending habit is working against your goals.",
    },
  },
  {
    id: 'meiling-c',
    name: 'Mei-Ling C.',
    title: 'Tax & Estate Planning Specialist',
    branch: 'North York Centre Branch',
    address: '5140 Yonge St, North York, ON',
    certs: ['CFP', 'TEP'],
    languages: ['English', 'Cantonese', 'Mandarin'],
    bio: 'Specializes in cross-border tax planning, estate transfers, and trust structures for families with complex financial situations.',
    photo: imgMeiling,
    rating: 4.8,
    reviews: 44,
    advisorType: 'wealth',
    specialties: ['protect', 'general'],
    styleStrength: ['context', 'challenge'],
    snapshotBonus: { over1m: 20, '250kto1m': 10 },
    styleMatch: {
      'Get to the point — direct and efficient': 'Mei-Ling distils complex tax rules into the three decisions that actually matter for your situation.',
      'Walk me through it — I like context': 'Mei-Ling maps out your full estate picture — tax triggers, beneficiary implications, trust options — before any recommendation.',
      "Be patient with me — I'm still learning": 'Mei-Ling has worked with many first-time estate-planning clients and explains every concept in plain language.',
      "Challenge my thinking — don't just agree with me": "Mei-Ling will flag tax risks in your current plan directly, even when it means a harder conversation.",
    },
  },
  {
    id: 'omar-b',
    name: 'Omar B.',
    title: 'Newcomer Financial Advisor',
    branch: 'Mississauga City Centre Branch',
    address: '100 City Centre Dr, Mississauga, ON',
    certs: ['PFP'],
    languages: ['English', 'Arabic', 'French'],
    bio: '6 years helping newcomers to Canada build credit, open accounts, and create their first financial plan in a new country.',
    photo: imgOmar,
    rating: 4.9,
    reviews: 112,
    advisorType: 'bank',
    specialties: ['general', 'debt'],
    styleStrength: ['patient', 'context'],
    snapshotBonus: { under50k: 20, preferNotToSay: 12 },
    styleMatch: {
      'Get to the point — direct and efficient': "Omar gives newcomers a clear priority list — what to set up first, second, and third — without overwhelming them.",
      'Walk me through it — I like context': 'Omar walks through how Canadian banking, credit scores, and tax filing work from scratch so nothing is assumed.',
      "Be patient with me — I'm still learning": 'Omar specializes in clients who are new to the Canadian financial system and never makes anyone feel behind.',
      "Challenge my thinking — don't just agree with me": "Omar will tell you directly if a financial habit from home won't work the same way in Canada.",
    },
  },
  {
    id: 'kayla-r',
    name: 'Kayla R.',
    title: 'Youth & First-Time Investor Advisor',
    branch: 'University District Branch',
    address: '256 Spadina Ave, Toronto, ON',
    certs: ['PFP'],
    languages: ['English', 'Spanish'],
    bio: 'Works exclusively with clients under 35: student debt, first TFSA, first RRSP, and building a savings habit that sticks.',
    photo: imgKayla,
    rating: 4.9,
    reviews: 78,
    advisorType: 'bank',
    specialties: ['invest', 'debt', 'general'],
    styleStrength: ['direct', 'patient'],
    snapshotBonus: { under50k: 18, '50to250k': 8 },
    styleMatch: {
      'Get to the point — direct and efficient': "Kayla gives you the three moves that will actually matter for your 20s or 30s — no product upsell.",
      'Walk me through it — I like context': 'Kayla walks through the TFSA vs. RRSP decision and student-debt paydown math so you understand the logic behind each choice.',
      "Be patient with me — I'm still learning": 'Kayla works almost exclusively with first-timers and meets every client without assumptions about what they already know.',
      "Challenge my thinking — don't just agree with me": "Kayla will push back on lifestyle-creep spending habits directly if they're working against your savings goals.",
    },
  },
  {
    id: 'sophie-c',
    name: 'Sophie C.',
    title: 'Retirement Income Specialist',
    branch: 'North York Branch',
    address: '5400 Yonge St, North York, ON',
    certs: ['CFP', 'PFP'],
    languages: ['English', 'Mandarin', 'Cantonese'],
    bio: '12 years helping clients transition from saving to drawing down wealth with confidence.',
    photo: null,
    rating: 4.9,
    reviews: 108,
    advisorType: 'bank',
    specialties: ['retire', 'invest'],
    styleStrength: ['context', 'patient'],
    snapshotBonus: { '250kto1m': 15, over1m: 10, '50to250k': 5 },
    styleMatch: {
      'Get to the point — direct and efficient': 'Sophie opens every meeting with a one-page income projection so you see the numbers before any discussion starts.',
      'Walk me through it — I like context': 'Sophie is known for walking clients through every withdrawal scenario — she wants you to understand the "why" behind each decision.',
      "Be patient with me — I'm still learning": 'Sophie has guided hundreds of first-time retirees and adapts her explanations to your pace without judgement.',
      "Challenge my thinking — don't just agree with me": 'Sophie actively stress-tests retirement assumptions and will tell you if a plan leaves you exposed.',
    },
  },
  {
    id: 'marcus-w',
    name: 'Marcus W.',
    title: 'Home Financing Advisor',
    branch: 'Mississauga City Centre Branch',
    address: '100 City Centre Dr, Mississauga, ON',
    certs: ['CFP'],
    languages: ['English', 'Spanish'],
    bio: 'Specializes in first-time buyers, renewals, and refinancing across the GTA.',
    photo: null,
    rating: 4.8,
    reviews: 93,
    advisorType: 'bank',
    specialties: ['home', 'debt'],
    styleStrength: ['direct', 'context'],
    snapshotBonus: { under50k: 8, '50to250k': 10 },
    styleMatch: {
      'Get to the point — direct and efficient': 'Marcus pre-qualifies you in the first 10 minutes and gives you a clear rate range before you leave.',
      'Walk me through it — I like context': 'Marcus walks through amortization tables and prepayment scenarios so you understand the full cost of each option.',
      "Be patient with me — I'm still learning": 'Marcus works with many first-time buyers and makes the mortgage process approachable from application to close.',
      "Challenge my thinking — don't just agree with me": "Marcus will flag if you're stretching too far and propose alternatives — he'd rather you qualify comfortably.",
    },
  },
  {
    id: 'aisha-o',
    name: 'Aisha O.',
    title: 'Senior Wealth Advisor',
    branch: 'Private Wealth Centre — King St',
    address: '66 King St W, Toronto, ON',
    certs: ['CFA', 'CIM', 'CFP'],
    languages: ['English', 'French'],
    bio: 'Guides high-net-worth individuals through complex portfolios, estate planning, and legacy goals.',
    photo: null,
    rating: 4.9,
    reviews: 44,
    advisorType: 'wealth',
    specialties: ['invest', 'protect', 'retire'],
    styleStrength: ['challenge', 'context'],
    snapshotBonus: { over1m: 30, '250kto1m': 18 },
    styleMatch: {
      'Get to the point — direct and efficient': "Aisha leads with recommendations and supports them with data — she won't pad the meeting.",
      'Walk me through it — I like context': "Aisha builds full financial maps for her clients so every recommendation has a visible rationale.",
      "Be patient with me — I'm still learning": "Aisha has worked with clients inheriting wealth for the first time and makes complexity manageable.",
      "Challenge my thinking — don't just agree with me": "Aisha is known for rigorous, evidence-based pushback — she'll tell you directly if a strategy carries more risk than you think.",
    },
  },
  {
    id: 'rajan-m',
    name: 'Rajan M.',
    title: 'Business & Investment Advisor',
    branch: 'Brampton Bramalea Branch',
    address: '25 Peel Centre Dr, Brampton, ON',
    certs: ['CFP'],
    languages: ['English', 'Hindi', 'Punjabi'],
    bio: 'Works with entrepreneurs and incorporated professionals to integrate business and personal finance.',
    photo: null,
    rating: 4.7,
    reviews: 62,
    advisorType: 'business',
    specialties: ['business', 'invest'],
    styleStrength: ['direct', 'challenge'],
    snapshotBonus: { '50to250k': 8, '250kto1m': 12 },
    styleMatch: {
      'Get to the point — direct and efficient': "Rajan comes with an agenda and leaves you with an action list — no filler.",
      'Walk me through it — I like context': 'Rajan maps out how your personal and business finances interact before recommending any structure.',
      "Be patient with me — I'm still learning": 'Rajan has helped many first-generation entrepreneurs understand corporate banking without jargon.',
      "Challenge my thinking — don't just agree with me": "Rajan will push back on incorporation or investment decisions that don't match your actual risk appetite.",
    },
  },
  {
    id: 'natalie-d',
    name: 'Natalie D.',
    title: 'Financial Planner',
    branch: 'Westmount Branch',
    address: '4060 Ste-Catherine St W, Westmount, QC',
    certs: ['CFP', 'PFP'],
    languages: ['English', 'French'],
    bio: 'Specialized in long-term financial independence planning for professionals and families navigating major life transitions.',
    photo: null,
    rating: 4.9,
    reviews: 89,
    advisorType: 'bank',
    specialties: ['invest', 'retire', 'general'],
    styleStrength: ['patient', 'context'],
    snapshotBonus: { '50to250k': 10, '250kto1m': 8 },
    styleMatch: {
      'Get to the point — direct and efficient': 'Natalie distils your situation into a clear priority order — what to tackle first, second, and third.',
      'Walk me through it — I like context': 'Natalie walks through the full financial picture before any recommendation so you understand every trade-off.',
      "Be patient with me — I'm still learning": "Natalie specializes in clients who are smart but new to investing — she never makes anyone feel behind.",
      "Challenge my thinking — don't just agree with me": "Natalie will surface the assumptions in your plan and stress-test them before you commit.",
    },
  },
  {
    id: 'carlos-v',
    name: 'Carlos V.',
    title: 'Home Equity & Mortgage Advisor',
    branch: 'Burnaby Metrotown Branch',
    address: '4700 Kingsway, Burnaby, BC',
    certs: ['CFP'],
    languages: ['English', 'Spanish'],
    bio: 'Expert in HELOC strategies, investment property financing, and refinancing for maximum flexibility.',
    photo: null,
    rating: 4.8,
    reviews: 76,
    advisorType: 'bank',
    specialties: ['home', 'invest', 'debt'],
    styleStrength: ['direct', 'challenge'],
    snapshotBonus: { '50to250k': 12, '250kto1m': 8 },
    styleMatch: {
      'Get to the point — direct and efficient': "Carlos gives you a clear rate and structure recommendation in the first 15 minutes — no runaround.",
      'Walk me through it — I like context': 'Carlos walks through equity calculations and HELOC scenarios so you understand every option before choosing.',
      "Be patient with me — I'm still learning": 'Carlos has guided many first-time investors through property financing and explains the math step by step.',
      "Challenge my thinking — don't just agree with me": "Carlos will tell you honestly if a refinancing strategy adds risk you're not accounting for.",
    },
  },
  {
    id: 'simone-t',
    name: 'Simone T.',
    title: 'Business Transition & Wealth Advisor',
    branch: 'Calgary Downtown Branch',
    address: '317 7th Ave SW, Calgary, AB',
    certs: ['CFP', 'CIM'],
    languages: ['English', 'French'],
    bio: 'Guides business owners through exit planning, corporate wealth extraction, and post-sale investment strategy.',
    photo: null,
    rating: 4.8,
    reviews: 51,
    advisorType: 'business',
    specialties: ['business', 'protect', 'invest'],
    styleStrength: ['challenge', 'direct'],
    snapshotBonus: { '250kto1m': 15, over1m: 20 },
    styleMatch: {
      'Get to the point — direct and efficient': 'Simone leads with the key tax and structure decisions — no preamble, straight to what matters.',
      'Walk me through it — I like context': 'Simone maps out the full business-exit timeline — from valuation to post-sale investment — before any recommendation.',
      "Be patient with me — I'm still learning": 'Simone has worked with many first-time sellers and explains corporate finance in plain language.',
      "Challenge my thinking — don't just agree with me": "Simone will push back on exit timelines or valuations that don't match market realities — she'd rather you hear it from her first.",
    },
  },
];

// ─── MATCH SCORING ───────────────────────────────────────────────────────────
// Returns advisors sorted best-match-first, each annotated with matchScore (0-100ish,
// clamped to a believable "match %") and whyMatch (the explanation to show the customer).
//
// `style` is a STYLE_OPTIONS entry ({ id, label, icon }) or null — id drives the
// styleStrength scoring bonus, label is the key into each advisor's styleMatch copy.

export function matchAdvisors(advisors, { category, style, snapshotBracket, advisorType } = {}) {
  const scored = advisors.map((advisor) => {
    let score = 55; // base — everyone's a reasonably good TD advisor
    if (category && advisor.specialties.includes(category)) score += 30;
    else if (category === 'general') score += 10;
    if (style?.id && advisor.styleStrength.includes(style.id)) score += 10;
    if (snapshotBracket && advisor.snapshotBonus[snapshotBracket]) score += advisor.snapshotBonus[snapshotBracket];
    if (advisorType && advisor.advisorType === advisorType) score += 20;

    const whyMatch = (style?.label && advisor.styleMatch[style.label]) || advisor.bio;
    return { ...advisor, matchScore: Math.min(99, score), whyMatch };
  });
  scored.sort((a, b) => b.matchScore - a.matchScore);
  return scored;
}
